import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCheckCircle,
  faArrowUp,
  faArrowDown,
  faCommentDots,
} from "@fortawesome/free-solid-svg-icons";

/* =====================
   DELTA (AUTO ARROW)
   ===================== */
const Delta = ({ delta, unit }) => {
  const num = Number(delta);
  const isUp = num > 0;
  const isDown = num < 0;

  return (
    <div
      className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full ${
        isUp ? " text-green-600" : isDown ? " text-red-600" : " text-gray-600"
      }`}
    >
      {(isUp || isDown) && (
        <FontAwesomeIcon
          icon={isUp ? faArrowUp : faArrowDown}
          className="text-xs"
        />
      )}
      <span>
        {delta}
        {unit}
      </span>
    </div>
  );
};

/* =====================
   HERO KPI (READ ONLY)
   ===================== */
const HeroKpi = ({ label, value, unit, delta, icon }) => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group">
    {/* Gradient accent on hover */}
    <div
      className="absolute top-0 left-0 w-full h-1 opacity-100 transition-opacity duration-300"
      style={{ background: "linear-gradient(90deg, #144677 0%, #1a5c99 100%)" }}
    ></div>

    <div className="flex items-center justify-between mb-4">
      <p className="text-xs font-bold tracking-wider text-gray-500 uppercase">
        {label}
      </p>
      <div
        className="p-2.5 rounded-xl transition-transform duration-300 group-hover:scale-110"
        style={{
          background: "linear-gradient(135deg, #144677 0%, #1a5c99 100%)",
        }}
      >
        <FontAwesomeIcon icon={icon} className="text-white text-lg" />
      </div>
    </div>

    <p className="text-5xl font-extrabold mb-4" style={{ color: "#144677" }}>
      {value}
      <span className="text-3xl">{unit}</span>
    </p>

    <Delta delta={delta} unit={unit} />
  </div>
);

/* =====================
   VOC HERO (MANUAL INPUT)
   ===================== */
const VocHeroInput = () => {
  const [value, setValue] = useState("");
  const [delta, setDelta] = useState("");
  const [editingValue, setEditingValue] = useState(false);
  const [editingDelta, setEditingDelta] = useState(false);

  return (
    <div className="bg-gradient-to-br from-[#144677] to-[#1a5c99] rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 relative overflow-hidden group">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-5 rounded-full -mr-16 -mt-16"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-5 rounded-full -ml-12 -mb-12"></div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-bold tracking-wider text-white/80 uppercase">
            VOC
          </p>
          <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
            <FontAwesomeIcon
              icon={faCommentDots}
              className="text-white text-lg"
            />
          </div>
        </div>

        {/* VALUE: edit mode vs display mode */}
        {editingValue ? (
          <input
            autoFocus
            type="number"
            placeholder="Enter VOC %"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => setEditingValue(false)}
            className="w-full text-5xl font-extrabold mb-4 outline-none bg-transparent text-white placeholder-white/40"
          />
        ) : (
          <div
            className="w-full text-5xl font-extrabold mb-4 cursor-text text-white"
            onClick={() => setEditingValue(true)}
          >
            {value === "" ? (
              <span className="text-white/30">Enter %</span>
            ) : (
              <>
                {value}
                <span className="text-3xl">%</span>
              </>
            )}
          </div>
        )}

        {/* DELTA: edit mode vs display mode */}
        <div className="mt-1">
          {editingDelta ? (
            <input
              autoFocus
              type="number"
              placeholder="Δ"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              onBlur={() => setEditingDelta(false)}
              className="text-sm px-3 py-1.5 outline-none bg-white/20 backdrop-blur-sm rounded-full w-24 text-white placeholder-white/50"
            />
          ) : (
            <div
              className="cursor-text inline-block"
              onClick={() => setEditingDelta(true)}
            >
              <div className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm">
                {delta !== "" && Number(delta) !== 0 && (
                  <FontAwesomeIcon
                    icon={Number(delta) > 0 ? faArrowUp : faArrowDown}
                    className="text-xs text-white"
                  />
                )}
                <span className="text-white">
                  {delta === "" ? "0%" : `${delta}%`}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* =====================
   MAIN COMPONENT
   ===================== */
const CustomerCarePerformance = ({ data }) => {
  const { avg_response_time, closed_24, closed_48 } = data;

  return (
    <div className="py-8 px-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* MANUAL VOC */}
          <VocHeroInput />

          {/* API KPI */}
          <HeroKpi
            label="AVG RESPONSE TIME"
            value={avg_response_time.value}
            unit="m"
            delta={avg_response_time.delta}
            icon={faClock}
          />

          <HeroKpi
            label="CLOSED ≤ 24 HOURS"
            value={closed_24.value}
            unit="%"
            delta={closed_24.delta}
            icon={faCheckCircle}
          />

          <HeroKpi
            label="CLOSED ≤ 48 HOURS"
            value={closed_48.value}
            unit="%"
            delta={closed_48.delta}
            icon={faCheckCircle}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomerCarePerformance;
